/**
 * ProductCalibrationsTab - Manage calibrations linked to a product
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/userPermissions';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Switch,
  FormControlLabel,
  Tooltip,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Visibility as ViewIcon,
  CheckCircle as ActiveIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import productsService, {
  ProductCalibrationLink,
  CalibrationListItem,
} from '../services/products';
import calibrationsService from '../services/calibrations';
import { apiErrorText } from '../utils/apiError';
import { rValue } from '../utils/regressionConversion';
import RemoveCalibrationDialog from './calibration/RemoveCalibrationDialog';
import { SectionActions } from './product/SectionCard';

const fmtRange = (min?: number | null, max?: number | null, unit?: string | null): string =>
  min == null || max == null ? '-' : `${Math.round(min)} to ${Math.round(max)}${unit ? ` ${unit}` : ''}`;

// Product2Calibration.Status codes (legacy workflow)
const LINK_STATUS_LABELS: Record<string, string> = {
  '1': 'In progress',
  '2': 'Finished',
};

interface ProductCalibrationsTabProps {
  productId: string;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  /** Embedded on the mix page: open a calibration in place instead of navigating. */
  onSelect?: (calibrationId: string) => void;
  /** Embedded: start a new calibration in place. */
  onNew?: () => void;
  /** The calibration open in the workspace (row highlighted). */
  selectedId?: string | null;
  /** Bump to reload the links (the workspace changed something). */
  refreshKey?: number;
  /** Tell the host after links load or change. */
  onLinksChanged?: (links: ProductCalibrationLink[]) => void;
  /** Mix design page: one-line active curve, the 3 most relevant rows, "Show N more". */
  compact?: boolean;
}

const COMPACT_ROWS = 3;

/** Active first, then finished, then the most test results. */
const relevantFirst = (links: ProductCalibrationLink[]): ProductCalibrationLink[] =>
  [...links].sort((a, b) =>
    Number(!!b.is_active) - Number(!!a.is_active)
    || Number(b.status === '2') - Number(a.status === '2')
    || (b.summary?.sample_count ?? 0) - (a.summary?.sample_count ?? 0));

const ProductCalibrationsTab: React.FC<ProductCalibrationsTabProps> = ({
  productId,
  onError,
  onSuccess,
  onSelect,
  onNew,
  selectedId,
  refreshKey,
  onLinksChanged,
  compact = false,
}) => {
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Mirrors the backend gates (utils/rbac.py): the server refuses these actions
  // for other roles, so they are not offered either.
  const canCreate = hasPermission(user, 'mix_designs.create_maturity_curve');
  const canLink = hasPermission(user, 'mix_designs.link_activate');
  const canDelete = hasPermission(user, 'mix_designs.edit_maturity_curve');
  const [links, setLinks] = useState<ProductCalibrationLink[]>([]);
  const [calibrations, setCalibrations] = useState<CalibrationListItem[]>([]);
  const [loading, setLoading] = useState(false);
  // False until the first load finishes, so "No calibrations yet" is not shown while loading.
  const [loaded, setLoaded] = useState(false);
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));

  // Link dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedCalibrationId, setSelectedCalibrationId] = useState('');
  const [linkIsActive, setLinkIsActive] = useState(false);

  // Remove (delete or unlink) dialog
  const [linkToRemove, setLinkToRemove] = useState<ProductCalibrationLink | null>(null);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productsService.listProductCalibrations(productId);
      setLinks(data);
      onLinksChanged?.(data);
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to load calibration links');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, onError, refreshKey]);

  const loadCalibrations = useCallback(async () => {
    try {
      const data = await productsService.listCalibrations();
      setCalibrations(data);
    } catch (err: any) {
      console.error('Failed to load calibrations:', err);
    }
  }, []);

  useEffect(() => {
    loadLinks();
    loadCalibrations();
  }, [loadLinks, loadCalibrations]);

  const handleLinkCalibration = async () => {
    if (!selectedCalibrationId) {
      onError('Please select a calibration');
      return;
    }

    setLoading(true);
    try {
      await productsService.linkCalibration(productId, {
        calibration_id: selectedCalibrationId,
        is_active: linkIsActive,
      });
      onSuccess('Calibration linked successfully');
      setLinkDialogOpen(false);
      setSelectedCalibrationId('');
      setLinkIsActive(false);
      loadLinks();
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to link calibration');
    } finally {
      setLoading(false);
    }
  };

  // Activation changes the strength every new pour of this mix shows, so it is confirmed.
  const [activateLink, setActivateLink] = useState<ProductCalibrationLink | null>(null);

  const handleToggleActive = async (link: ProductCalibrationLink) => {
    if (!link.is_active && activateLink?.id !== link.id) {
      setActivateLink(link);
      return;
    }
    setActivateLink(null);
    setLoading(true);
    try {
      if (!link.is_active) {
        // Activating runs the FULL workflow (legacy ActiveCalibration):
        // recalculates the regression and rewrites the product's default
        // targets with the chosen curve — not just the link flag.
        const result = await calibrationsService.activate(link.calibration_id, productId);
        onSuccess(result.message || 'Calibration activated');
      } else {
        await productsService.updateCalibrationLink(productId, link.id, {
          is_active: false,
        });
        onSuccess('Calibration deactivated. Targets keep their last calculated values.');
      }
      loadLinks();
    } catch (err: any) {
      onError(apiErrorText(err, 'Failed to update calibration link'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoved = (message: string) => {
    setLinkToRemove(null);
    onSuccess(message);
    loadLinks();
    loadCalibrations();
  };

  // Filter out already-linked calibrations
  const linkedCalIds = new Set(links.map(l => l.calibration_id));
  const availableCalibrations = calibrations.filter(c => !linkedCalIds.has(c.id));

  // Pieces of one calibration row, shared by the table and the phone list.
  const visibleLinks = compact
    ? (showAll ? relevantFirst(links) : relevantFirst(links).slice(0, COMPACT_ROWS))
    : links;

  const nameBlock = (link: ProductCalibrationLink) => (
    <>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" fontWeight="medium" sx={{ overflowWrap: 'anywhere' }}>
          {link.calibration_name || link.calibration_code || link.calibration_id}
        </Typography>
        {(link.version_no ?? 1) > 1 && (
          <Chip label={`v${link.version_no}`} size="small" variant="outlined" />
        )}
      </Box>
      {(link.calibration_name || compact) && (
        <Typography variant="caption" color="text.secondary">
          {[
            link.calibration_name ? link.calibration_code : null,
            compact && link.summary ? (link.summary.calibration_type === 's' ? 'System' : 'Manual') : null,
          ].filter(Boolean).join(' · ')}
        </Typography>
      )}
    </>
  );

  const agesChip = (link: ProductCalibrationLink) => (link.summary ? (
    <Tooltip title="ASTM C1074 recommends 5 test ages (1, 3, 7, 14 and 28 days)">
      <Chip
        size="small"
        variant="outlined"
        color={link.summary.ages_tested >= link.summary.ages_recommended ? 'success' : 'default'}
        label={`${link.summary.ages_tested} of ${link.summary.ages_recommended} ages`}
      />
    </Tooltip>
  ) : null);

  const statusChip = (link: ProductCalibrationLink) => (link.status ? (
    <Chip
      label={LINK_STATUS_LABELS[link.status] || link.status}
      size="small"
      color={link.status === '2' ? 'success' : 'default'}
    />
  ) : null);

  // Activation requires a finished calibration (server 409s otherwise)
  const activeSwitch = (link: ProductCalibrationLink) => (
    <Tooltip title={!link.is_active && link.status !== '2' ? 'Finish this calibration before activating' : ''}>
      <span>
        <Switch
          checked={!!link.is_active}
          onChange={() => handleToggleActive(link)}
          disabled={loading || !canLink || (!link.is_active && link.status !== '2')}
          size="small"
          inputProps={{ 'aria-label': `active: ${link.calibration_name || link.calibration_code}` }}
        />
      </span>
    </Tooltip>
  );

  const rowActions = (link: ProductCalibrationLink) => (
    <>
      <Tooltip title="Open">
        <IconButton
          size="small"
          color="primary"
          aria-label="open calibration"
          onClick={(e) => {
            e.stopPropagation();
            if (onSelect) onSelect(link.calibration_id);
            else navigate(`/calibrations/${link.calibration_id}?productId=${productId}`);
          }}
        >
          <ViewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {(canLink || canDelete) && (
        <Tooltip title="Delete or unlink">
          <span>
            <IconButton
              size="small"
              color="error"
              aria-label="delete or unlink calibration"
              onClick={() => setLinkToRemove(link)}
              disabled={loading}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  );

  return (
    <Box>
      {/* Section title comes from the host page; the buttons go in its card header */}
      <SectionActions>
        <>
          {canCreate && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => (onNew ? onNew() : navigate(`/calibrations/new?productId=${productId}`))}
              sx={{ textTransform: 'none' }}
            >
              {compact ? 'New' : 'New Calibration'}
            </Button>
          )}
          {canLink && (
            <Button
              variant={compact ? 'outlined' : 'contained'}
              size="small"
              startIcon={<LinkIcon />}
              onClick={() => setLinkDialogOpen(true)}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              {compact ? 'Link existing' : 'Link Calibration'}
            </Button>
          )}
        </>
      </SectionActions>

      {compact && links.length > 0 && (() => {
        const active = links.find((l) => l.is_active);
        const sm = active?.summary;
        const r = rValue(sm?.r2);
        const meta = sm
          ? [
            sm.curve_type === 'linear' ? 'Linear curve' : 'Logarithmic curve',
            r != null ? `R ${r.toFixed(2)}` : null,
            sm.maturity_min != null ? `tested ${fmtRange(sm.maturity_min, sm.maturity_max, sm.unit)}` : null,
          ].filter(Boolean).join(' · ')
          : '';
        return (
          <Box
            role="status"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              borderRadius: 1.5,
              border: 1,
              borderColor: active ? 'success.light' : 'warning.light',
              bgcolor: (theme) => alpha(active ? theme.palette.success.main : theme.palette.warning.main, 0.06),
              px: 1.5,
              py: 1.25,
              mb: 2,
            }}
          >
            {active
              ? <ActiveIcon fontSize="small" sx={{ color: 'success.main', mt: '2px' }} />
              : <WarningIcon fontSize="small" sx={{ color: 'warning.main', mt: '2px' }} />}
            {active ? (
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2">
                  <strong>Active:</strong> {active.calibration_name || active.calibration_code}
                </Typography>
                {meta && <Typography variant="caption" color="text.secondary">{meta}</Typography>}
              </Box>
            ) : (
              <Typography variant="body2">
                <strong>None active.</strong> Pours of this mix show maturity but no estimated strength until a
                finished calibration is made active.
              </Typography>
            )}
          </Box>
        );
      })()}

      {!compact && (() => {
        const active = links.find((l) => l.is_active);
        const sm = active?.summary;
        if (!active) {
          return links.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No calibration is active, so pours of this mix show maturity but no estimated strength.
              Finish a calibration and make it active.
            </Alert>
          ) : null;
        }
        const r = rValue(sm?.r2);
        if (compact) {
          return null;
        }
        return (
          <Alert severity="success" icon={false} sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Active curve:</strong> {active.calibration_name || active.calibration_code}
              {sm ? `, ${sm.curve_type === 'linear' ? 'linear' : 'logarithmic'}${r != null ? ` (R = ${r.toFixed(2)})` : ''}` : ''}
            </Typography>
            {sm && (
              <Typography variant="caption" display="block">
                Tested range {fmtRange(sm.maturity_min, sm.maturity_max, sm.unit)} from {sm.ages_tested} test age
                {sm.ages_tested === 1 ? '' : 's'}. {sm.basis_text ? `Maturity equation: ${sm.basis_text}.` : ''}
                {' '}Strengths outside the tested range are shown as below or above it.
              </Typography>
            )}
          </Alert>
        );
      })()}

      {!loaded ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} aria-label="loading calibrations" />
        </Box>
      ) : links.length === 0 ? (
        compact ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No calibrations yet. Start a new one from test results, or link one that already exists.
          </Typography>
        ) : (
          <Alert severity="info">No calibrations linked to this product.</Alert>
        )
      ) : compact && narrow ? (
        // Phones: one stacked item per calibration instead of a squeezed table.
        <Box component="ul" aria-label="calibrations" sx={{ listStyle: 'none', m: 0, p: 0, mx: -2 }}>
          {visibleLinks.map((link) => (
            <Box
              component="li"
              key={link.id}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}
            >
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                {nameBlock(link)}
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                  {statusChip(link)}
                  {agesChip(link)}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Active</Typography>
                  {activeSwitch(link)}
                </Box>
                <Box>{rowActions(link)}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <TableContainer
          component={compact ? Box : Paper}
          {...(compact ? { sx: { mx: { xs: -2, sm: -2.5 }, width: 'auto', overflowX: 'auto' } } : { variant: 'outlined' as const })}
        >
          <Table size={compact ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {!compact && <TableCell>Type</TableCell>}
                <TableCell>Test ages</TableCell>
                {!compact && <TableCell>Tested maturity range</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleLinks.map((link) => (
                <TableRow
                  key={link.id}
                  selected={!!selectedId && link.calibration_id === selectedId}
                  hover={!!onSelect}
                  sx={onSelect ? { cursor: 'pointer' } : undefined}
                  onClick={onSelect ? () => onSelect(link.calibration_id) : undefined}
                >
                  <TableCell>{nameBlock(link)}</TableCell>
                  {!compact && (
                    <TableCell>
                      {link.summary ? (link.summary.calibration_type === 's' ? 'System' : 'Manual') : '-'}
                    </TableCell>
                  )}
                  <TableCell>{agesChip(link) ?? '-'}</TableCell>
                  {!compact && (
                    <TableCell>
                      {link.summary ? fmtRange(link.summary.maturity_min, link.summary.maturity_max, link.summary.unit) : '-'}
                    </TableCell>
                  )}
                  <TableCell>{statusChip(link)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>{activeSwitch(link)}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                    {rowActions(link)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {compact && links.length > COMPACT_ROWS && (
        <Button size="small" sx={{ mt: 0.5, textTransform: 'none' }} onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer' : `Show ${links.length - COMPACT_ROWS} more`}
        </Button>
      )}

      {/* Link Calibration Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Calibration to Product</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Calibration</InputLabel>
              <Select
                value={selectedCalibrationId}
                onChange={(e) => setSelectedCalibrationId(e.target.value)}
                label="Calibration"
              >
                {availableCalibrations.length === 0 ? (
                  <MenuItem disabled>No available calibrations</MenuItem>
                ) : (
                  availableCalibrations.map((cal) => (
                    <MenuItem key={cal.id} value={cal.id}>
                      {cal.code} {cal.name ? `- ${cal.name}` : ''}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={linkIsActive}
                  onChange={(e) => setLinkIsActive(e.target.checked)}
                />
              }
              label="Set as active calibration"
            />
            <Typography variant="caption" color="text.secondary">
              Only a finished calibration can be made active. Link a calibration that is in progress first, then finish it and activate it.
            </Typography>
            {linkIsActive && (
              <Alert severity="warning" variant="outlined">
                Setting this as active will deactivate any currently active calibration for this product.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLinkCalibration} variant="contained" disabled={loading || !selectedCalibrationId}>
            Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate confirmation */}
      <Dialog open={!!activateLink} onClose={() => setActivateLink(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Make this calibration active?</DialogTitle>
        <DialogContent>
          <Typography>
            New pours of this mix design will estimate strength from{' '}
            <strong>{activateLink?.calibration_name || activateLink?.calibration_code}</strong>, and the strength
            targets of the mix are recalculated from its curve. The calibration that is active now becomes inactive.
            Running pours keep the calibration they started with.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateLink(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={loading}
            onClick={() => activateLink && handleToggleActive(activateLink)}
          >
            Make active
          </Button>
        </DialogActions>
      </Dialog>

      <RemoveCalibrationDialog
        calibrationId={linkToRemove?.calibration_id ?? null}
        productId={productId}
        linkId={linkToRemove?.id}
        canDelete={canDelete}
        canUnlink={canLink}
        onClose={() => setLinkToRemove(null)}
        onDeleted={handleRemoved}
        onUnlinked={handleRemoved}
      />
    </Box>
  );
};

export default ProductCalibrationsTab;
